// ===========================================
// Subscription Modal - Add/Edit Global Subscriptions
// ===========================================
// Modal for managing shared dev costs like Cursor, ChatGPT, Vercel, etc.

import React, { useState, useEffect } from 'react';
import { X, DollarSign, Bot, Cloud, Database, Activity, Monitor, HardDrive, MoreHorizontal, Calendar } from 'lucide-react';
import './SubscriptionModal.scss';

// Subscription type configuration with icons
const subscriptionTypes = [
  { value: 'ide', label: 'IDE', icon: Monitor },
  { value: 'ai', label: 'AI / LLM', icon: Bot },
  { value: 'hosting', label: 'Hosting', icon: Cloud },
  { value: 'database', label: 'Database', icon: Database },
  { value: 'monitoring', label: 'Monitoring', icon: Activity },
  { value: 'storage', label: 'Storage', icon: HardDrive },
  { value: 'other', label: 'Other', icon: MoreHorizontal }
];

// Quick presets for common subscriptions
const presets = [
  { name: 'Cursor Pro', type: 'ide', monthlyCost: 20 },
  { name: 'Cursor Ultra', type: 'ide', monthlyCost: 200 },
  { name: 'ChatGPT Plus', type: 'ai', monthlyCost: 20 },
  { name: 'ChatGPT Pro', type: 'ai', monthlyCost: 200 },
  { name: 'Claude Pro', type: 'ai', monthlyCost: 20 },
  { name: 'GitHub Copilot', type: 'ai', monthlyCost: 10 },
  { name: 'Vercel Pro', type: 'hosting', monthlyCost: 20 },
  { name: 'Netlify Pro', type: 'hosting', monthlyCost: 19 },
  { name: 'Railway', type: 'hosting', monthlyCost: 5 },
  { name: 'DigitalOcean', type: 'hosting', monthlyCost: 5 },
  { name: 'AWS', type: 'hosting', monthlyCost: 0 },
  { name: 'MongoDB Atlas', type: 'database', monthlyCost: 0 },
  { name: 'PlanetScale', type: 'database', monthlyCost: 0 },
  { name: 'Supabase', type: 'database', monthlyCost: 0 },
  { name: 'Datadog', type: 'monitoring', monthlyCost: 0 },
  { name: 'Sentry', type: 'monitoring', monthlyCost: 0 }
];

const SubscriptionModal = ({ isOpen, onClose, onSave, subscription = null }) => {
  // Form state - initialized with subscription data or defaults
  const [formData, setFormData] = useState({
    name: '',
    type: 'other',
    startDate: '', // When the subscription started - used to calculate total spent
    monthlyCost: '',
    billingCycle: 'monthly',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Helper to calculate total spent based on start date and monthly cost
  const calculateTotalSpent = () => {
    if (!formData.startDate || !formData.monthlyCost) return null;
    
    const start = new Date(formData.startDate);
    const now = new Date();
    // Calculate months difference (inclusive of current month)
    const monthsDiff = (now.getFullYear() - start.getFullYear()) * 12 + 
                       (now.getMonth() - start.getMonth());
    
    // Calculate effective monthly cost (convert yearly to monthly if needed)
    const monthlyCost = formData.billingCycle === 'yearly' 
      ? Number(formData.monthlyCost) / 12 
      : Number(formData.monthlyCost);
    
    // Total spent = months * monthly cost (minimum 1 month if start date is in current month)
    const totalSpent = Math.max(0, monthsDiff + 1) * monthlyCost;
    return totalSpent;
  };

  // Reset form when modal opens/closes or subscription changes
  useEffect(() => {
    if (subscription) {
      // Editing mode - populate with existing data
      // Format date for input[type=date] (YYYY-MM-DD)
      const formattedDate = subscription.startDate 
        ? new Date(subscription.startDate).toISOString().split('T')[0] 
        : '';
      setFormData({
        name: subscription.name || '',
        type: subscription.type || 'other',
        startDate: formattedDate,
        monthlyCost: subscription.monthlyCost?.toString() || '',
        billingCycle: subscription.billingCycle || 'monthly',
        notes: subscription.notes || ''
      });
    } else {
      // Add mode - reset to defaults
      setFormData({
        name: '',
        type: 'other',
        startDate: '',
        monthlyCost: '',
        billingCycle: 'monthly',
        notes: ''
      });
    }
    setError('');
  }, [subscription, isOpen]);

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // Apply a preset to the form
  const applyPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      name: preset.name,
      type: preset.type,
      monthlyCost: preset.monthlyCost.toString()
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Subscription name is required');
      return;
    }
    if (!formData.monthlyCost || isNaN(Number(formData.monthlyCost))) {
      setError('Valid monthly cost is required');
      return;
    }

    setSaving(true);

    const result = await onSave({
      name: formData.name.trim(),
      type: formData.type,
      startDate: formData.startDate || null, // Include start date for total spent calculation
      monthlyCost: Number(formData.monthlyCost),
      billingCycle: formData.billingCycle,
      notes: formData.notes.trim()
    });

    setSaving(false);

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Failed to save subscription');
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="subscription-modal__overlay" onClick={onClose}>
      <div className="subscription-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="subscription-modal__header">
          <h2>
            <DollarSign size={20} />
            {subscription ? 'Edit Subscription' : 'Add Subscription'}
          </h2>
          <button className="subscription-modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Quick Presets - only show when adding */}
        {!subscription && (
          <div className="subscription-modal__presets">
            <span className="subscription-modal__presets-label">Quick Add:</span>
            <div className="subscription-modal__presets-list">
              {presets.slice(0, 8).map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="subscription-modal__preset"
                  onClick={() => applyPreset(preset)}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="subscription-modal__form">
          {/* Error Message */}
          {error && (
            <div className="subscription-modal__error">{error}</div>
          )}

          {/* Name */}
          <div className="subscription-modal__field">
            <label htmlFor="name">Service Name *</label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Cursor Ultra, ChatGPT Pro"
              autoFocus
            />
          </div>

          {/* Type */}
          <div className="subscription-modal__field">
            <label>Category</label>
            <div className="subscription-modal__types">
              {subscriptionTypes.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    className={`subscription-modal__type ${formData.type === type.value ? 'active' : ''}`}
                    onClick={() => handleChange('type', type.value)}
                  >
                    <Icon size={16} />
                    <span>{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date Started - when the subscription began (for total spent calculation) */}
          <div className="subscription-modal__field">
            <label htmlFor="startDate">
              <Calendar size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              Date Started
            </label>
            <input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
            />
          </div>

          {/* Billing Cycle and Cost - cycle first so label updates correctly */}
          <div className="subscription-modal__row">
            <div className="subscription-modal__field">
              <label htmlFor="billing">Billing Cycle</label>
              <select
                id="billing"
                value={formData.billingCycle}
                onChange={(e) => handleChange('billingCycle', e.target.value)}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            <div className="subscription-modal__field">
              <label htmlFor="cost">
                {formData.billingCycle === 'yearly' ? 'Yearly Cost *' : 'Monthly Cost *'}
              </label>
              <div className="subscription-modal__cost-input">
                <span className="subscription-modal__currency">$</span>
                <input
                  id="cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.monthlyCost}
                  onChange={(e) => handleChange('monthlyCost', e.target.value)}
                  placeholder={formData.billingCycle === 'yearly' ? '240.00' : '20.00'}
                />
              </div>
              {/* Help text to clarify the conversion */}
              {formData.billingCycle === 'yearly' && formData.monthlyCost && (
                <span className="subscription-modal__cost-hint">
                  ≈ ${(Number(formData.monthlyCost) / 12).toFixed(2)}/mo
                </span>
              )}
            </div>
          </div>

          {/* Total Spent - calculated from start date and monthly cost */}
          {formData.startDate && formData.monthlyCost && (
            <div className="subscription-modal__total-spent">
              <span className="subscription-modal__total-spent-label">Total Spent:</span>
              <span className="subscription-modal__total-spent-value">
                ${calculateTotalSpent()?.toFixed(2)}
              </span>
              <span className="subscription-modal__total-spent-hint">
                (since {new Date(formData.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})
              </span>
            </div>
          )}

          {/* Notes */}
          <div className="subscription-modal__field">
            <label htmlFor="notes">Notes (optional)</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any notes about this subscription..."
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="subscription-modal__actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : subscription ? 'Update' : 'Add Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionModal;

